import React, { Suspense, lazy } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import config from "./coreFIles/config";
import { isAdminLoggedIn } from "./coreFIles/auth";

import Layout from "./directives/layout";
import Dashboard from "./component/dashboard";
import Users from "./component/users";
import UserForm from "./component/user-form";
import UserDetail from "./component/user-detail";
import UserOnboardingAnswers from "./component/user-onboarding-answers";
import UserGrowthSnapshotAnswers from "./component/user-growth-snapshot-answers";
import Purchases from "./component/purchases";
import CourseProgressReport from "./component/course-progress-report";
import Coupons from "./component/coupons";
import CouponRedemptions from "./component/coupon-redemptions";
import UserActivity from "./component/user-activity";
import CouponForm from "./component/coupon-form";
import CourseSales from "./component/course-sales";
import CourseSaleForm from "./component/course-sale-form";
import CoachingHacks from "./component/coaching-hacks";
import CoachingHackForm from "./component/coaching-hack-form";
import DailyPrompts from "./component/daily-prompts";
import DailyPromptForm from "./component/daily-prompt-form";
import GrowthSnapshots from "./component/growth-snapshots";
import GrowthSnapshotForm from "./component/growth-snapshot-form";
import GrowthSnapshotSections from "./component/growth-snapshot-sections";
import GrowthSnapshotSectionForm from "./component/growth-snapshot-section-form";
import GrowthSnapshotSteps from "./component/growth-snapshot-steps";
import GrowthSnapshotStepForm from "./component/growth-snapshot-step-form";
import GrowthSnapshotQuestions from "./component/growth-snapshot-questions";
import GrowthSnapshotQuestionForm from "./component/growth-snapshot-question-form";
import ReflectionsScreensHub from "./component/reflections-screens-hub";
import CategoryReflectionsList from "./component/category-reflections-list";
import CategoryReflectionForm from "./component/category-reflection-form";
import OverallReflectionsList from "./component/overall-reflections-list";
import OverallReflectionForm from "./component/overall-reflection-form";
import StaticScreensList from "./component/static-screens-list";
import StaticScreenForm from "./component/static-screen-form";
import Courses from "./component/courses";
import CourseForm from "./component/course-form";
import CourseWeeks from "./component/course-weeks";
import CourseWeekForm from "./component/course-week-form";
import WeekContent from "./component/week-content";
import CourseOnboardingSteps from "./component/course-onboarding-steps";
import CourseOnboardingStepForm from "./component/course-onboarding-step-form";
import CourseStepSections from "./component/course-step-sections";
import CourseStepSectionForm from "./component/course-step-section-form";
import StepSectionQuestions from "./component/step-section-questions";
import StepSectionQuestionForm from "./component/step-section-question-form";
import InfoVideos from "./component/info-videos";
import InfoVideoForm from "./component/info-video-form";
import CmsContent from "./component/cms-content";
import CmsContentForm from "./component/cms-content-form";
import SalesContentList from "./component/sales-content-list";
import SalesContentForm from "./component/sales-content-form";
import ResourceHub from "./component/resource-hub";
import ResourceHubForm from "./component/resource-hub-form";
import HelpQuestions from "./component/help-questions";
import HelpQuestionForm from "./component/help-question-form";
import Settings from "./component/settings";
import XeroSettings from "./component/xero-settings";
import Login from "./component/login";
import ForgotPassword from "./component/forgot-password";
import ComingSoon from "./component/coming-soon";
import NotFound from "./component/not-found";

/** Legal pages only: Bootstrap + public layout. Not in the main admin bundle. */
const TermsConditions = lazy(() =>
  import(/* webpackChunkName: "public-legal" */ "./component/terms-conditions")
);
const PrivacyPolicy = lazy(() =>
  import(/* webpackChunkName: "public-legal" */ "./component/privacy-policy")
);
const HelpSupport = lazy(() =>
  import(/* webpackChunkName: "public-legal" */ "./component/help-support")
);
const SalesPage = lazy(() =>
  import(/* webpackChunkName: "public-legal" */ "./component/sales-page")
);
const ResetPassword = lazy(() =>
  import(/* webpackChunkName: "public-legal" */ "./component/reset-password")
);

function PublicLegalFallback() {
  return (
    <div style={{ minHeight: "40vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 12 }}>
      <div className="loading-spinner" />
      <p style={{ margin: 0, fontSize: 14, color: "#64748b" }}>Loading…</p>
    </div>
  );
}

function PublicLegalRoute({ Page }) {
  return (
    <Suspense fallback={<PublicLegalFallback />}>
      <Page />
    </Suspense>
  );
}

function RequireAuth({ children, loginPath }) {
  const location = useLocation();
  if (!isAdminLoggedIn()) {
    return <Navigate to={loginPath} replace state={{ from: location }} />;
  }
  return children;
}

function App() {
  const base = (config.baseUrl || "/").replace(/\/+$/, "") || "/";
  const loginPath = `${base}/login`.replace("//", "/");
  const forgotPath = `${base}/forgot-password`.replace("//", "/");
  const comingSoonPath = `${base}/coming-soon`.replace("//", "/");
  const termsPath = `${base}/terms-conditions`.replace("//", "/");
  const privacyPath = `${base}/privacy-policy`.replace("//", "/");
  const helpPath = `${base}/help-support`.replace("//", "/");
  const salesPath = `${base}/sales`.replace("//", "/");
  const resetPasswordPath = `${base}/reset-password`.replace("//", "/");

  return (
    <BrowserRouter>
      <Routes>
        {/* Public routes (no auth) */}
        <Route path={loginPath} element={<Login />} />
        <Route path={forgotPath} element={<ForgotPassword />} />
        <Route path={comingSoonPath} element={<ComingSoon />} />
        <Route path={termsPath} element={<PublicLegalRoute Page={TermsConditions} />} />
        <Route path={privacyPath} element={<PublicLegalRoute Page={PrivacyPolicy} />} />
        <Route path={helpPath} element={<PublicLegalRoute Page={HelpSupport} />} />
        <Route path={salesPath} element={<PublicLegalRoute Page={SalesPage} />} />
        <Route path={resetPasswordPath} element={<PublicLegalRoute Page={ResetPassword} />} />

        {/* Protected app routes (everything else) */}
        <Route
          path={base}
          element={
            <RequireAuth loginPath={loginPath}>
              <Layout />
            </RequireAuth>
          }
        >
          <Route index element={<Navigate to={`${base}/dashboard`.replace("//", "/")} replace />} />

          <Route path="dashboard" element={<Dashboard />} />

          <Route path="users" element={<Users />} />
          <Route path="users/new" element={<UserForm mode="create" />} />
          <Route path="users/:userId" element={<UserDetail />} />
          <Route path="users/:userId/onboarding-answers/:phase" element={<UserOnboardingAnswers />} />
          <Route path="users/:userId/growth-snapshot-answers/:phase" element={<UserGrowthSnapshotAnswers />} />
          <Route path="users/:userId/edit" element={<UserForm mode="edit" />} />

          <Route path="course-progress" element={<CourseProgressReport />} />
          <Route path="purchases" element={<Purchases />} />

          <Route path="coupons" element={<Coupons />} />
          <Route path="coupons/new" element={<CouponForm mode="create" />} />
          <Route path="coupons/:id/edit" element={<CouponForm mode="edit" />} />
          <Route path="coupon-redemptions" element={<CouponRedemptions />} />
          <Route path="user-activity" element={<UserActivity />} />
          <Route path="course-sales" element={<CourseSales />} />
          <Route path="course-sales/new" element={<CourseSaleForm mode="create" />} />
          <Route path="course-sales/:saleId/edit" element={<CourseSaleForm mode="edit" />} />

          <Route path="coaching-hacks" element={<CoachingHacks />} />
          <Route path="coaching-hacks/new" element={<CoachingHackForm mode="create" />} />
          <Route path="coaching-hacks/:hackId/edit" element={<CoachingHackForm mode="edit" />} />

          <Route path="daily-prompts" element={<DailyPrompts />} />
          <Route path="daily-prompts/new" element={<DailyPromptForm mode="create" />} />
          <Route path="daily-prompts/:promptId/edit" element={<DailyPromptForm mode="edit" />} />

          <Route path="growth-snapshots" element={<GrowthSnapshots />} />
          <Route path="growth-snapshots/new" element={<GrowthSnapshotForm mode="create" />} />
          <Route path="growth-snapshots/:snapshotId/edit" element={<GrowthSnapshotForm mode="edit" />} />
          <Route path="growth-snapshots/:snapshotId/sections" element={<GrowthSnapshotSections />} />
          <Route
            path="growth-snapshots/:snapshotId/content"
            element={<Navigate to="/reflections-screens" replace />}
          />
          <Route path="growth-snapshots/:snapshotId/sections/new" element={<GrowthSnapshotSectionForm mode="create" />} />
          <Route path="growth-snapshots/:snapshotId/sections/:sectionId/edit" element={<GrowthSnapshotSectionForm mode="edit" />} />
          <Route path="growth-snapshots/:snapshotId/sections/:sectionId/steps" element={<GrowthSnapshotSteps />} />
          <Route path="growth-snapshots/:snapshotId/sections/:sectionId/steps/new" element={<GrowthSnapshotStepForm mode="create" />} />
          <Route path="growth-snapshots/:snapshotId/sections/:sectionId/steps/:stepId/edit" element={<GrowthSnapshotStepForm mode="edit" />} />
          <Route path="growth-snapshots/:snapshotId/sections/:sectionId/steps/:stepId/questions" element={<GrowthSnapshotQuestions />} />
          <Route path="growth-snapshots/:snapshotId/sections/:sectionId/steps/:stepId/questions/new" element={<GrowthSnapshotQuestionForm mode="create" />} />
          <Route
            path="growth-snapshots/:snapshotId/sections/:sectionId/steps/:stepId/questions/:questionId/edit"
            element={<GrowthSnapshotQuestionForm mode="edit" />}
          />
          <Route path="growth-snapshots/:snapshotId" element={<Navigate to="sections" replace />} />

          <Route path="reflections-screens" element={<ReflectionsScreensHub />} />
          <Route path="reflections-screens/category-reflections" element={<CategoryReflectionsList />} />
          <Route
            path="reflections-screens/category-reflections/new"
            element={<CategoryReflectionForm mode="create" />}
          />
          <Route
            path="reflections-screens/category-reflections/:reflectionId/edit"
            element={<CategoryReflectionForm mode="edit" />}
          />
          <Route path="reflections-screens/overall-reflections" element={<OverallReflectionsList />} />
          <Route
            path="reflections-screens/overall-reflections/new"
            element={<OverallReflectionForm mode="create" />}
          />
          <Route
            path="reflections-screens/overall-reflections/:reflectionId/edit"
            element={<OverallReflectionForm mode="edit" />}
          />
          <Route path="reflections-screens/static-screens" element={<StaticScreensList />} />
          <Route path="reflections-screens/static-screens/new" element={<StaticScreenForm mode="create" />} />
          <Route
            path="reflections-screens/static-screens/:screenId/edit"
            element={<StaticScreenForm mode="edit" />}
          />

          {/* Journals hidden for now */}
          <Route path="journals" element={<Navigate to={`${base}/dashboard`.replace("//", "/")} replace />} />

          <Route path="courses" element={<Courses />} />
          <Route path="courses/new" element={<CourseForm mode="create" />} />
          <Route path="courses/:courseId/edit" element={<CourseForm mode="edit" />} />
          <Route path="courses/:courseId/weeks" element={<CourseWeeks />} />
          <Route path="courses/:courseId/weeks/new" element={<CourseWeekForm mode="create" />} />
          <Route path="courses/:courseId/weeks/:weekId/edit" element={<CourseWeekForm mode="edit" />} />
          <Route path="courses/:courseId/weeks/:weekId" element={<WeekContent />} />
          <Route path="courses/:courseId/steps" element={<CourseOnboardingSteps />} />
          <Route path="courses/:courseId/steps/new" element={<CourseOnboardingStepForm mode="create" />} />
          <Route path="courses/:courseId/steps/:stepId/edit" element={<CourseOnboardingStepForm mode="edit" />} />

          <Route path="step-sections" element={<CourseStepSections />} />
          <Route path="step-sections/new" element={<CourseStepSectionForm mode="create" />} />
          <Route path="step-sections/:sectionId/questions" element={<StepSectionQuestions />} />
          <Route path="step-sections/:sectionId/questions/new" element={<StepSectionQuestionForm mode="create" />} />
          <Route
            path="step-sections/:sectionId/questions/:questionId/edit"
            element={<StepSectionQuestionForm mode="edit" />}
          />
          <Route path="step-sections/:sectionId/edit" element={<CourseStepSectionForm mode="edit" />} />

          <Route path="info-videos" element={<InfoVideos />} />
          <Route path="info-videos/new" element={<InfoVideoForm mode="create" />} />
          <Route path="info-videos/:id/edit" element={<InfoVideoForm mode="edit" />} />

          <Route path="cms-content" element={<CmsContent />} />
          <Route path="cms-content/new" element={<CmsContentForm mode="create" />} />
          <Route path="cms-content/:id/edit" element={<CmsContentForm mode="edit" />} />

          <Route path="sales-content" element={<SalesContentList />} />
          <Route path="sales-content/new" element={<SalesContentForm mode="create" />} />
          <Route path="sales-content/:id/edit" element={<SalesContentForm mode="edit" />} />

          <Route path="resource-hub" element={<ResourceHub />} />
          <Route path="resource-hub/new" element={<ResourceHubForm mode="create" />} />
          <Route path="resource-hub/:id/edit" element={<ResourceHubForm mode="edit" />} />

          <Route path="help-questions" element={<HelpQuestions />} />
          <Route path="help-questions/new" element={<HelpQuestionForm mode="create" />} />
          <Route path="help-questions/:id/edit" element={<HelpQuestionForm mode="edit" />} />

          <Route path="settings" element={<Settings />} />
          <Route path="xero-settings" element={<XeroSettings />} />

          <Route path="*" element={<NotFound />} />
        </Route>

        {/* Any other URL should still end up behind auth */}
        <Route path="*" element={<Navigate to={base} replace />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;

